import React, { useMemo } from 'react';
import { ConfigColors } from 'libs/end-to-end/src/types/branding/colors';
import { GraphLegend } from 'apps/sigma2-ui/src/components/ui/GraphLegend';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2GroupedBarChart } from 'apps/sigma2-ui/src/components/S2GroupedBarChart';
import { ValueDifferencePopoverLayer } from 'apps/sigma2-ui/src/components/ValueDifferencePopoverLayer';
import { AxisMarkersLayer } from 'apps/sigma2-ui/src/components/AxisMarkersLayer';
import { RootCauseAddingVariablesGraphGroupResult } from 'libs/end-to-end/src/root-cause-analysis';
import { capitalizeFirstLetterInEachSentence } from 'libs/end-to-end/src/string-formatting';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { BarChartBarPlaceholderLayer } from 'apps/sigma2-ui/src/components/BarCharBarPlaceholderLayer';
import { getLevelPairsFromResult } from 'libs/end-to-end/src/root-cause-analysis';
import { determineUnitOfMetric } from 'libs/end-to-end/src/metrics';

const ChartLayers = [
  ValueDifferencePopoverLayer,
  AxisMarkersLayer,
  BarChartBarPlaceholderLayer,
];

const ChartMargins = {
  top: 8,
  right: 20,
  bottom: 24,
  left: 65,
};

export const OaxacaBarChart = ({
  result,
  download,
  focusArea,
  maxYAxisLabelWidthPx,
}: {
  result: RootCauseAddingVariablesGraphGroupResult;
  download: React.ReactNode;
  focusArea: FocusArea;
  maxYAxisLabelWidthPx?: number;
}) => {
  const keys = useMemo(() => {
    return [
      result.treatmentDimensionLevelColumnValue,
      result.referenceDimensionLevelColumnValue,
    ];
  }, [result]);

  // come up with a hex color that is pleasant for all keys
  const colors = useMemo(() => {
    const colorOptions = Object.entries(ConfigColors).map(
      ([, v]) => v.Primary.raw,
    );

    return keys.map((_, i) => colorOptions[i % colorOptions.length]);
  }, [keys]);

  const groups = useMemo(() => {
    const uniqueGroupsByName = getLevelPairsFromResult(result);

    return uniqueGroupsByName.map((group) => ({
      id: group.name,
      label: group.name,
      groupLevels: [
        {
          id: 'treatmentLevel',
          value:
            group.treatment_level * (result.outcomeMetricIsBoolean ? 100 : 1),
        },
        {
          id: 'referenceLevel',
          value:
            group.reference_level * (result.outcomeMetricIsBoolean ? 100 : 1),
        },
      ],
    }));
  }, [result]);

  const levels = useMemo(() => {
    return [
      {
        id: 'treatmentLevel',
        label: result.treatmentDimensionLevelColumnValue,
      },
      {
        id: 'referenceLevel',
        label: result.referenceDimensionLevelColumnValue,
      },
    ];
  }, [
    result.referenceDimensionLevelColumnValue,
    result.treatmentDimensionLevelColumnValue,
  ]);

  const units = useMemo(() => {
    return determineUnitOfMetric({
      knownMaximum: null,
      isMetricBoolean: result.outcomeMetricIsBoolean,
      metricFocusArea: focusArea,
      definitionUnit: undefined,
    });
  }, [focusArea, result.outcomeMetricIsBoolean]);

  const getExtraMarginRight = React.useCallback((maxXLength: number) => {
    return 5 * (maxXLength / 2);
  }, []);

  const getExtraMarginBottom = React.useCallback(
    (xAxisLabel: string | null | undefined) => {
      return xAxisLabel ? 30 : 0;
    },
    [],
  );

  const getExtraMarginLeft = React.useCallback((maxYLength: number) => {
    return 10 * (maxYLength / 2);
  }, []);

  return (
    <>
      <div className="px-2 mb-4">
        <Spacing direction="horizontal" size="justify-between">
          <Spacing
            direction="horizontal"
            size="lg"
            className="justify-center w-full"
          >
            <GraphLegend color={colors[0]}>
              {result.treatmentDimensionLevelColumnValue}
            </GraphLegend>
            <GraphLegend color={colors[1]}>
              {result.referenceDimensionLevelColumnValue}
            </GraphLegend>
          </Spacing>

          {download}
        </Spacing>
      </div>
      <div className="flex flex-col">
        <S2GroupedBarChart
          xGroups={groups}
          xLevels={levels}
          xAxisLabel={capitalizeFirstLetterInEachSentence(
            `Estimated ${result.outcomeMetricDisplayName}`,
          )}
          yAxisLabel={capitalizeFirstLetterInEachSentence(
            result.controlVariableDisplayName,
          )}
          units={units}
          horizontalMinBarWidth={Infinity}
          verticalBarFixedThickness={false}
          verticalBarThicknessAsFactorOfWidth={0.06}
          verticalBarMinThickness={8}
          verticalBarMaxThickness={50}
          verticalMinHeight={390}
          verticalMaxHeight={815}
          verticalInnerPadding={2}
          verticalGroupPaddingAsFactorOfHeight={0.25}
          layers={ChartLayers}
          hoverEffect={false}
          margin={ChartMargins}
          getExtraMarginRight={getExtraMarginRight}
          getExtraMarginBottom={getExtraMarginBottom}
          getExtraMarginLeft={getExtraMarginLeft}
          maxYAxisLabelWidthPx={maxYAxisLabelWidthPx}
        />
      </div>
    </>
  );
};
